import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import AIEnhance from "@/components/AIEnhance";
import AudioBubble from "@/components/AudioBubble";
import MessageComposer from "@/components/MessageComposer";
import {
  Target, Plus, X, Sparkles, Loader2, Send, ClipboardList, NotebookPen,
  MessageSquare, CalendarClock, ListChecks, ShieldCheck, Info, LifeBuoy, Save,
} from "lucide-react";
import { toast } from "sonner";

export default function ProPatientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [plan, setPlan] = useState(null);
  const [activities, setActivities] = useState([]);
  const [records, setRecords] = useState([]);
  const [messages, setMessages] = useState([]);
  const [points, setPoints] = useState([]);

  const loadAll = async () => {
    const [pd, act, rec, msg, pts] = await Promise.all([
      api.get(`/patients/${id}`),
      api.get(`/activities?patient_id=${id}`),
      api.get(`/records?patient_id=${id}`),
      api.get(`/messages?patient_id=${id}`),
      api.get(`/next-points?patient_id=${id}`),
    ]);
    setPatient(pd.data.patient); setPlan(pd.data.plan);
    setActivities(act.data); setRecords(rec.data); setMessages(msg.data); setPoints(pts.data);
  };
  useEffect(() => { loadAll(); }, [id]);

  if (!patient) return <div className="grid place-items-center h-64"><Loader2 className="animate-spin text-[#0F766E]" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-5 animate-fade-up">
      <Card className="rounded-2xl p-5 border-[#E0F2FE] flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-[#0F766E] text-white grid place-items-center text-xl font-bold">{patient.name[0]}</div>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-[#1F3B4D]">{patient.name}</h1>
          <p className="text-sm text-[#94A3B8]">{patient.email}</p>
        </div>
        <div className="flex flex-wrap gap-1.5 justify-end">
          {(plan?.modules_active || []).map((m) => <Badge key={m} variant="secondary" className="text-[11px]">{m.replace(/_/g, " ")}</Badge>)}
        </div>
      </Card>

      <Tabs defaultValue="plano">
        <TabsList className="rounded-full bg-white border border-[#E0F2FE] flex-wrap h-auto p-1">
          {[
            ["plano", "Plano", Target], ["atividades", "Atividades", ClipboardList],
            ["registros", "Registros", NotebookPen], ["mensagens", "Mensagens", MessageSquare],
            ["preparacao", "Preparação", ListChecks], ["auditoria", "IA & Auditoria", ShieldCheck],
            ["ajuda", "Pedidos de ajuda", LifeBuoy],
          ].map(([v, l, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-full data-[state=active]:bg-[#0F766E] data-[state=active]:text-white gap-1.5">
              <Icon size={14} /> {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="plano" className="mt-5"><PlanTab id={id} plan={plan} onSaved={loadAll} /></TabsContent>
        <TabsContent value="atividades" className="mt-5"><ActivitiesTab id={id} activities={activities} onChanged={loadAll} /></TabsContent>
        <TabsContent value="registros" className="mt-5"><RecordsTab id={id} records={records} onChanged={loadAll} /></TabsContent>
        <TabsContent value="mensagens" className="mt-5"><MessagesTab id={id} user={user} messages={messages} onChanged={loadAll} /></TabsContent>
        <TabsContent value="preparacao" className="mt-5"><PrepTab id={id} points={points} /></TabsContent>
        <TabsContent value="auditoria" className="mt-5"><AuditTab id={id} /></TabsContent>
        <TabsContent value="ajuda" className="mt-5"><HelpRequestsTab id={id} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Plano ---------------- */
function PlanTab({ id, plan, onSaved }) {
  const [objectives, setObjectives] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [personalReasons, setPersonalReasons] = useState([]);
  const [na, setNa] = useState({ title: "", description: "" });
  const [initialNa, setInitialNa] = useState({ title: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [savingNa, setSavingNa] = useState(false);

  useEffect(() => {
    setObjectives(plan?.objectives || []); setPriorities(plan?.priorities || []);
    setStrategies(plan?.strategies || []); setPersonalReasons(plan?.personal_reasons || []);
    setNa(plan?.next_action || { title: "", description: "" });
    setInitialNa(plan?.next_action || { title: "", description: "" });
  }, [plan]);

  const addItem = (setter, list) => setter([...list, { id: crypto.randomUUID(), title: "" }]);
  const upd = (setter, list, i, val) => { const c = [...list]; c[i] = { ...c[i], title: val }; setter(c); };
  const del = (setter, list, i) => setter(list.filter((_, x) => x !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/plans/${id}`, {
        objectives: objectives.filter((o) => o.title.trim()),
        priorities: priorities.filter((p) => p.title.trim()),
        strategies: strategies.filter((s) => s.title.trim()),
        personal_reasons: personalReasons.filter((r) => r.title.trim()),
        next_action: na, modules_active: plan?.modules_active || [],
      });
      toast.success("Plano atualizado."); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const naDirty = JSON.stringify({ t: na.title || "", d: na.description || "" }) !== JSON.stringify({ t: initialNa.title || "", d: initialNa.description || "" });

  const saveNextAction = async () => {
    if (savingNa || !naDirty) return;
    setSavingNa(true);
    try {
      await api.put(`/plans/${id}`, {
        objectives: objectives.filter((o) => o.title.trim()),
        priorities: priorities.filter((p) => p.title.trim()),
        strategies: strategies.filter((s) => s.title.trim()),
        personal_reasons: personalReasons.filter((r) => r.title.trim()),
        next_action: na, modules_active: plan?.modules_active || [],
      });
      setInitialNa({ title: na.title || "", description: na.description || "" });
      toast.success("Próxima ação atualizada.");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingNa(false); }
  };

  const List = ({ title, list, setter, testid }) => (
    <Card className="rounded-2xl p-5 border-[#E0F2FE]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[#1F3B4D]">{title}</h3>
        <Button size="sm" variant="outline" className="rounded-full border-[#E0F2FE] gap-1" onClick={() => addItem(setter, list)} data-testid={`add-${testid}`}><Plus size={14} /> Adicionar</Button>
      </div>
      <div className="space-y-2">
        {list.map((it, i) => (
          <div key={it.id || i} className="flex gap-2">
            <Input value={it.title} onChange={(e) => upd(setter, list, i, e.target.value)} data-testid={`${testid}-input-${i}`} className="rounded-xl border-[#E0F2FE]" />
            <Button size="icon" variant="ghost" onClick={() => del(setter, list, i)}><X size={16} className="text-[#94A3B8]" /></Button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhum item.</p>}
      </div>
    </Card>
  );

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl p-5 border-0 text-white" style={{ background: "linear-gradient(120deg,#0F766E,#1F3B4D)" }} data-testid="plan-next-action">
        <div className="flex items-center gap-2 text-[#34D399] text-xs font-semibold uppercase tracking-wider mb-2"><Target size={14} /> Próxima ação do paciente</div>
        <Input value={na.title} onChange={(e) => setNa({ ...na, title: e.target.value })} data-testid="next-action-title"
          placeholder="Ex.: Registrar por 3 dias o que sinto antes da vontade de apostar" className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-xl mb-2" />
        <Textarea value={na.description || ""} onChange={(e) => setNa({ ...na, description: e.target.value })} data-testid="next-action-desc"
          placeholder="Orientação breve…" className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-xl" />
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <AIEnhance text={`${na.title}\n${na.description || ""}`} patientId={id} fieldType="próxima ação"
            onApply={(t) => { const [f, ...r] = t.split("\n"); setNa({ title: f, description: r.join("\n").trim() }); }} testid="ai-next-action" onDark />
          <Button onClick={saveNextAction} disabled={!naDirty || savingNa} data-testid="save-next-action-button"
            className="rounded-full bg-white text-[#0F766E] hover:bg-white/90 gap-2 font-semibold disabled:opacity-50">
            {savingNa ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Salvar
          </Button>
        </div>
      </Card>
      <div className="grid md:grid-cols-2 gap-4">
        <List title="Objetivos" list={objectives} setter={setObjectives} testid="objective" />
        <List title="Prioridades atuais" list={priorities} setter={setPriorities} testid="priority" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="rounded-2xl p-5 border-[#E0F2FE]">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-[#1F3B4D]">Estratégias combinadas</h3>
            <Button size="sm" variant="outline" className="rounded-full border-[#E0F2FE] gap-1" onClick={() => setStrategies([...strategies, { id: crypto.randomUUID(), title: "", context: "general" }])} data-testid="add-strategy"><Plus size={14} /> Adicionar</Button>
          </div>
          <p className="text-xs text-[#94A3B8] mb-3">Aparecem primeiro nas telas de acolhimento "Pedir ajuda".</p>
          <div className="space-y-2">
            {strategies.map((it, i) => (
              <div key={it.id || i} className="flex gap-2">
                <Input value={it.title} onChange={(e) => { const c = [...strategies]; c[i] = { ...c[i], title: e.target.value }; setStrategies(c); }} data-testid={`strategy-input-${i}`} placeholder="Estratégia" className="rounded-xl border-[#E0F2FE]" />
                <Select value={it.context || "general"} onValueChange={(v) => { const c = [...strategies]; c[i] = { ...c[i], context: v }; setStrategies(c); }}>
                  <SelectTrigger className="rounded-xl border-[#E0F2FE] w-28 shrink-0" data-testid={`strategy-context-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urge">Fissura</SelectItem>
                    <SelectItem value="anxiety">Ansiedade</SelectItem>
                    <SelectItem value="general">Geral</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="icon" variant="ghost" onClick={() => setStrategies(strategies.filter((_, x) => x !== i))}><X size={16} className="text-[#94A3B8]" /></Button>
              </div>
            ))}
            {strategies.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhuma estratégia.</p>}
          </div>
        </Card>
        <List title="Motivos pessoais" list={personalReasons} setter={setPersonalReasons} testid="reason" />
      </div>
      <Button onClick={save} disabled={saving} data-testid="save-plan-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2">
        {saving ? <Loader2 size={16} className="animate-spin" /> : null} Salvar plano
      </Button>
    </div>
  );
}

/* ---------------- Atividades ---------------- */
function ActivitiesTab({ id, activities, onChanged }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", instructions: "", type: "text" });
  const [reviewFor, setReviewFor] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [analysis, setAnalysis] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Informe um título."); return; }
    try {
      await api.post("/activities", { patient_id: id, ...form, visibility: "SHARED" });
      toast.success("Atividade enviada ao paciente."); setCreating(false); setForm({ title: "", instructions: "", type: "text" }); onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openReview = (a) => { setReviewFor(a); setFeedback(a.review?.feedback || ""); setAnalysis(""); };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const content = reviewFor.response?.transcript || reviewFor.response?.text || reviewFor.title;
      const { data } = await api.post("/ai/analyze", { content, patient_id: id });
      setAnalysis(data.analysis);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const submitReview = async () => {
    try {
      await api.post(`/activities/${reviewFor.id}/review`, { feedback });
      toast.success("Retorno enviado ao paciente."); setReviewFor(null); onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addToNext = async () => {
    const content = reviewFor.response?.transcript || reviewFor.response?.text || reviewFor.title;
    await api.post("/next-points", { patient_id: id, content, source_type: "atividade" });
    toast.success("Adicionado à próxima sessão.");
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setCreating(true)} data-testid="create-activity-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2"><Plus size={16} /> Nova atividade</Button>
      <div className="space-y-3">
        {activities.map((a) => (
          <Card key={a.id} data-testid={`pro-activity-${a.id}`} className="rounded-2xl p-4 border-[#E0F2FE] flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="capitalize text-[11px]">{a.type}</Badge>
                <Badge className={`border-0 text-[11px] ${a.status === "submitted" ? "bg-[#60A5FA]/15 text-[#2563eb]" : a.status === "reviewed" ? "bg-[#34D399]/15 text-[#0F766E]" : "bg-[#F59E0B]/15 text-[#b26a00]"}`}>{a.status}</Badge>
              </div>
              <div className="font-semibold text-[#1F3B4D] mt-1">{a.title}</div>
              {a.response && <p className="text-sm text-[#1F3B4D]/70 mt-1 line-clamp-2">{a.response.transcript || a.response.text}</p>}
            </div>
            {a.status !== "pending" && <Button size="sm" variant="outline" className="rounded-full border-[#E0F2FE]" onClick={() => openReview(a)} data-testid={`open-review-${a.id}`}>Analisar</Button>}
          </Card>
        ))}
        {activities.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhuma atividade.</p>}
      </div>

      {/* create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-[#1F3B4D]">Nova atividade</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="activity-title-input" placeholder="Título" className="rounded-xl border-[#E0F2FE]" />
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="rounded-xl border-[#E0F2FE]" data-testid="activity-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Texto</SelectItem>
                <SelectItem value="audio">Áudio</SelectItem>
                <SelectItem value="image">Imagem / Foto</SelectItem>
                <SelectItem value="pdf">Documento / PDF</SelectItem>
              </SelectContent>
            </Select>
            <Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} data-testid="activity-instructions-input" placeholder="Instruções para o paciente…" className="rounded-xl border-[#E0F2FE] min-h-[100px]" />
            <AIEnhance text={form.instructions || form.title} patientId={id} fieldType="atividade"
              onApply={(t) => setForm({ ...form, instructions: t })} testid="ai-activity" label="Melhorar / Criar com IA" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={create} data-testid="submit-new-activity" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2"><Send size={15} /> Enviar ao paciente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* review dialog */}
      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[92vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-heading text-[#1F3B4D]">{reviewFor?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Card className="rounded-xl p-3 bg-[#F8FAFC] border-[#E0F2FE]">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Resposta do paciente</div>
              <p className="text-sm text-[#1F3B4D]">{reviewFor?.response?.transcript || reviewFor?.response?.text || "—"}</p>
              {(reviewFor?.response?.file_ids || []).map((fid) => (
                <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs px-3 py-1.5 rounded-full bg-[#E0F2FE] text-[#0F766E]">ver anexo</a>
              ))}
            </Card>

            <div className="flex gap-2 flex-wrap">
              <Button onClick={analyze} disabled={analyzing} variant="outline" data-testid="ai-analyze-button" className="rounded-full border-[#34D399] text-[#0F766E] gap-1.5">
                {analyzing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Apoio de IA (analisar)
              </Button>
              <Button onClick={addToNext} variant="outline" data-testid="add-to-next-session-button" className="rounded-full border-[#E0F2FE] gap-1.5"><ListChecks size={15} /> Adicionar à próxima sessão</Button>
            </div>

            {analysis && (
              <Card className="rounded-xl p-4 bg-[#34D399]/8 border-[#34D399]/30" data-testid="ai-analysis-result">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-[#0F766E] mb-1 flex items-center gap-1"><Sparkles size={13} /> Apoio de IA</div>
                <p className="text-sm text-[#1F3B4D] whitespace-pre-wrap">{analysis}</p>
              </Card>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-[#0F766E]">Retorno ao paciente</label>
                <AIEnhance text={feedback} patientId={id} fieldType="orientação" onApply={setFeedback} testid="ai-feedback" />
              </div>
              <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid="review-feedback-input" placeholder="Escreva ou fale seu retorno…" className="rounded-xl border-[#E0F2FE] min-h-[110px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewFor(null)}>Fechar</Button>
            <Button onClick={submitReview} disabled={!feedback.trim()} data-testid="submit-review-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2"><Send size={15} /> Enviar retorno</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Registros ---------------- */
function RecordsTab({ id, records, onChanged }) {
  const addToNext = async (r) => {
    await api.post("/next-points", { patient_id: id, content: r.transcript || r.text || "Registro", source_type: "registro" });
    toast.success("Adicionado à próxima sessão.");
  };
  return (
    <div className="space-y-3">
      {records.map((r) => (
        <Card key={r.id} data-testid={`pro-record-${r.id}`} className="rounded-2xl p-4 border-[#E0F2FE] flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1"><Badge variant="secondary" className="capitalize text-[11px]">{r.content_type}</Badge><span className="text-xs text-[#94A3B8]">{new Date(r.created_at).toLocaleString("pt-BR")}</span></div>
            <p className="text-sm text-[#1F3B4D]">{r.transcript || r.text}</p>
            {r.file_id && <a href={fileUrl(r.file_id)} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs px-3 py-1.5 rounded-full bg-[#E0F2FE] text-[#0F766E]">ver anexo</a>}
          </div>
          <Button size="sm" variant="outline" className="rounded-full border-[#E0F2FE] shrink-0" onClick={() => addToNext(r)}>+ próxima sessão</Button>
        </Card>
      ))}
      {records.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhum registro do paciente.</p>}
    </div>
  );
}

/* ---------------- Mensagens ---------------- */
function MessagesTab({ id, user, messages, onChanged }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [messages]);
  return (
    <Card className="rounded-2xl border-[#E0F2FE] p-4 flex flex-col h-[60vh]">
      <div className="flex-1 overflow-auto space-y-3" data-testid="pro-messages-thread">
        {messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#0F766E] text-white" : "bg-[#E0F2FE] text-[#1F3B4D]"}`}>
                {m.type === "audio"
                  ? <AudioBubble fileId={m.audio_file_id} duration={m.duration} transcript={m.transcript} mine={mine} />
                  : <p className="text-sm whitespace-pre-wrap">{m.text}</p>}
                <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#94A3B8]"}`}>{new Date(m.created_at).toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}</div>
              </div>
            </div>
          );
        })}
        {messages.length === 0 && <p className="text-center text-sm text-[#94A3B8] mt-6">Nenhuma mensagem ainda.</p>}
        <div ref={endRef} />
      </div>
      <div className="pt-3 border-t border-[#E0F2FE] mt-3">
        <MessageComposer patientId={id} enableAI onSent={onChanged} />
      </div>
    </Card>
  );
}

/* ---------------- Preparação ---------------- */
function PrepTab({ id, points }) {
  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (ai) => {
    setLoading(true);
    try { const { data } = await api.get(`/sessions/prep/${id}?ai=${ai}`); setPrep(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(false); }, [id]);

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl p-5 border-[#E0F2FE]">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#1F3B4D] flex items-center gap-2"><ListChecks size={18} className="text-[#0F766E]" /> Preparação da próxima sessão</h3>
          <Button size="sm" onClick={() => load(true)} disabled={loading} data-testid="ai-prep-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-1.5">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Resumo com IA
          </Button>
        </div>
        {prep?.ai_summary && (
          <Card className="mt-4 rounded-xl p-4 bg-[#34D399]/8 border-[#34D399]/30" data-testid="ai-prep-summary">
            <p className="text-sm text-[#1F3B4D] whitespace-pre-wrap">{prep.ai_summary}</p>
          </Card>
        )}
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="rounded-2xl p-4 border-[#E0F2FE]">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#0F766E] mb-2">Pontos marcados p/ próxima sessão</div>
          <div className="space-y-2">
            {points.map((p) => <div key={p.id} className="text-sm text-[#1F3B4D] flex gap-2"><span className="text-[#34D399]">•</span><span>{p.content}</span></div>)}
            {points.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhum ponto marcado.</p>}
          </div>
        </Card>
        <Card className="rounded-2xl p-4 border-[#E0F2FE]">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#0F766E] mb-2">Atividades</div>
          <p className="text-sm text-[#1F3B4D]">{prep?.activities_done?.length || 0} realizadas · {prep?.activities_pending?.length || 0} pendentes</p>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#0F766E] mt-3 mb-1">Pedidos de apoio abertos</div>
          <p className="text-sm text-[#1F3B4D]">{prep?.support?.length || 0}</p>
        </Card>
      </div>
    </div>
  );
}

/* ---------------- Auditoria ---------------- */
function AuditTab({ id }) {
  const [gens, setGens] = useState([]);
  useEffect(() => { api.get(`/ai/generations?patient_id=${id}`).then((r) => setGens(r.data)); }, [id]);
  return (
    <div className="space-y-3">
      <Card className="rounded-2xl p-4 border-[#E0F2FE] bg-[#E0F2FE]/40 flex items-start gap-2">
        <Info size={16} className="text-[#0F766E] mt-0.5 shrink-0" />
        <p className="text-sm text-[#1F3B4D]/80">Todo conteúdo gerado por IA é registrado para auditoria: quem solicitou, tipo, texto original, contexto utilizado e resultado. Nada é enviado ao paciente sem sua ação explícita.</p>
      </Card>
      {gens.map((g) => (
        <Card key={g.id} data-testid={`audit-${g.id}`} className="rounded-2xl p-4 border-[#E0F2FE]">
          <div className="flex items-center gap-2 mb-1"><Badge variant="secondary" className="text-[11px]">{g.generation_type}</Badge><span className="text-xs text-[#94A3B8]">{new Date(g.created_at).toLocaleString("pt-BR")}</span></div>
          <div className="text-xs text-[#94A3B8]">Original</div>
          <p className="text-sm text-[#1F3B4D]/70 mb-2 line-clamp-2">{g.original_text}</p>
          <div className="text-xs text-[#94A3B8]">Resultado</div>
          <p className="text-sm text-[#1F3B4D] line-clamp-3">{g.result}</p>
          {g.context_used?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{g.context_used.map((c, i) => <Badge key={i} className="text-[10px] bg-[#E0F2FE] text-[#0F766E] border-0">{c}</Badge>)}</div>}
        </Card>
      ))}
      {gens.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhuma geração de IA registrada.</p>}
    </div>
  );
}

/* ---------------- Pedidos de ajuda ---------------- */
const HELP_LABELS = {
  substance_or_gambling_urge: "Vontade de beber, usar ou jogar",
  anxiety: "Ansiedade",
  counselor_request: "Falar com o conselheiro",
  support_network_request: "Rede de apoio",
  emergency: "Atendimento de urgência",
};

function HelpRequestsTab({ id }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/help-requests?patient_id=${id}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [id]);
  const resolve = async (rid) => { await api.post(`/help-requests/${rid}/resolve`); toast.success("Pedido marcado como resolvido."); load(); };

  return (
    <div className="space-y-3">
      {items.map((h) => (
        <Card key={h.id} data-testid={`help-req-${h.id}`} className="rounded-2xl p-4 border-[#E0F2FE]">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className="bg-[#EF4444]/10 text-[#dc2626] border-0 text-[11px]">{HELP_LABELS[h.trigger_context] || h.trigger_context}</Badge>
                <Badge variant="secondary" className="text-[11px] capitalize">{(h.status || "").replace("_", " ")}</Badge>
                <span className="text-xs text-[#94A3B8]">{new Date(h.created_at).toLocaleString("pt-BR")}</span>
              </div>
              {h.message && <p className="text-sm text-[#1F3B4D] mt-1">"{h.message}"</p>}
              {h.recipients?.length > 0 && (
                <p className="text-xs text-[#94A3B8] mt-1">Para: {h.recipients.map((r) => r.name).join(", ")}</p>
              )}
              {h.deliveries?.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {h.deliveries.map((d, i) => <Badge key={i} className="text-[10px] bg-[#E0F2FE] text-[#0F766E] border-0">{d.name}: {(d.status || "").replace("_", " ")}</Badge>)}
                </div>
              )}
            </div>
            {h.status !== "resolved" && (
              <Button size="sm" variant="outline" className="rounded-full border-[#E0F2FE] shrink-0" onClick={() => resolve(h.id)} data-testid={`resolve-help-${h.id}`}>Resolver</Button>
            )}
          </div>
        </Card>
      ))}
      {items.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhum pedido de ajuda registrado.</p>}
    </div>
  );
}
